(function () {
	const GUEST_STORAGE_KEY = "wishlist_product_ids:guest";
	const CUSTOMER_STORAGE_KEY_PREFIX = "wishlist_product_ids:customer:";
	// The old `wishlist_product_ids` key is intentionally left untouched. Its
	// owner is unknowable, so scoped storage is authoritative going forward.
	const BUTTON_SELECTOR = "[data-wishlist-button][data-product-id]";
	const COUNT_SELECTOR = "[data-wishlist-count]";
	const PAGE_CONTAINER_SELECTOR = "[data-wishlist-page]";
	const RESULTS_CONTAINER_SELECTOR = "#WishlistResults";
	const RESULTS_FRAGMENT_SELECTOR = "[data-wishlist-results]";
	const EMPTY_SELECTOR = "[data-wishlist-empty]";
	const LOADING_SELECTOR = "[data-wishlist-loading]";
	const SEARCH_CHUNK_SIZE = 50;

	function parseId(value) {
		const n = Number.parseInt(String(value || ""), 10);
		return Number.isFinite(n) && n > 0 ? n : null;
	}

	function normalizeIds(ids) {
		const set = new Set();
		(Array.isArray(ids) ? ids : []).forEach((value) => {
			const id = parseId(value);
			if (id) {
				set.add(id);
			}
		});
		return Array.from(set);
	}

	function getCustomerId() {
		const rawId = window.igCustomerId;
		const customerId =
			rawId === null || rawId === undefined ? "" : String(rawId).trim();

		return /^\d+$/.test(customerId) && !/^0+$/.test(customerId)
			? customerId
			: null;
	}

	function getActiveStorageKey() {
		if (window.igCustomerLoggedIn === false) return GUEST_STORAGE_KEY;
		if (window.igCustomerLoggedIn !== true) return null;

		const customerId = getCustomerId();
		return customerId ? `${CUSTOMER_STORAGE_KEY_PREFIX}${customerId}` : null;
	}

	function writeStoredIds(storageKey, ids) {
		if (!storageKey) return false;

		try {
			localStorage.setItem(storageKey, JSON.stringify(normalizeIds(ids)));
			return true;
		} catch (error) {
			return false;
		}
	}

	function readStoredIds(storageKey) {
		if (!storageKey) return [];

		try {
			const raw = localStorage.getItem(storageKey);
			if (!raw) return [];
			const parsed = JSON.parse(raw);
			if (!Array.isArray(parsed)) {
				writeStoredIds(storageKey, []);
				return [];
			}
			const ids = normalizeIds(parsed);
			if (ids.length !== parsed.length) {
				writeStoredIds(storageKey, ids);
			}
			return ids;
		} catch (error) {
			return [];
		}
	}

	function getIds() {
		const storageKey = getActiveStorageKey();
		if (!storageKey) return [];

		const activeIds = readStoredIds(storageKey);
		if (storageKey === GUEST_STORAGE_KEY) return activeIds;

		const guestIds = readStoredIds(GUEST_STORAGE_KEY);
		if (guestIds.length === 0) return activeIds;

		const mergedIds = normalizeIds(activeIds.concat(guestIds));
		if (!writeStoredIds(storageKey, mergedIds)) return activeIds;

		try {
			localStorage.removeItem(GUEST_STORAGE_KEY);
		} catch (error) {
			// The customer copy is durable; a later merge remains deduplicated.
		}
		return mergedIds;
	}

	function emitUpdate(ids) {
		const normalized = normalizeIds(ids);
		document.dispatchEvent(
			new CustomEvent("wishlist:updated", {
				detail: {
					ids: normalized,
					count: normalized.length,
				},
			}),
		);
	}

	function setIds(ids) {
		const normalized = normalizeIds(ids);
		const storageKey = getActiveStorageKey();
		const storedIds = writeStoredIds(storageKey, normalized)
			? normalized
			: readStoredIds(storageKey);
		emitUpdate(storedIds);
		return storedIds;
	}

	function hasId(id) {
		const value = parseId(id);
		if (!value) return false;
		return getIds().includes(value);
	}

	function addId(id) {
		const value = parseId(id);
		if (!value) return getIds();
		const ids = getIds();
		if (!ids.includes(value)) {
			ids.push(value);
		}
		return setIds(ids);
	}

	function removeId(id) {
		const value = parseId(id);
		if (!value) return getIds();
		const ids = getIds().filter((item) => item !== value);
		return setIds(ids);
	}

	function toggleId(id) {
		const value = parseId(id);
		if (!value) return getIds();
		if (hasId(value)) {
			return removeId(value);
		}
		return addId(value);
	}

	function count() {
		return getIds().length;
	}

	function labelForState(active) {
		return active ? "Remove from wishlist" : "Add to wishlist";
	}

	function setButtonState(button, active) {
		button.classList.toggle("is-active", active);
		button.setAttribute("aria-pressed", active ? "true" : "false");
		button.setAttribute("aria-label", labelForState(active));
	}

	function updateButtons(ids) {
		const idSet = new Set(ids);
		document.querySelectorAll(BUTTON_SELECTOR).forEach((button) => {
			const productId = parseId(button.getAttribute("data-product-id"));
			if (!productId) return;
			setButtonState(button, idSet.has(productId));
		});
	}

	function updateCountBubble(nextCount) {
		document.querySelectorAll(COUNT_SELECTOR).forEach((el) => {
			const value = Number(nextCount) || 0;
			if (value > 0) {
				el.textContent = String(value);
				el.hidden = false;
			} else {
				el.textContent = "0";
				el.hidden = true;
			}
		});
	}

	function updateGlobalUi(ids) {
		updateButtons(ids);
		updateCountBubble(ids.length);
	}

	const PAGE_COUNT_SELECTOR = "[data-wishlist-page-count]";

	function getWishlistPageElements() {
		const page = document.querySelector(PAGE_CONTAINER_SELECTOR);
		if (!page) return null;
		return {
			page,
			resultsContainer: page.querySelector(RESULTS_CONTAINER_SELECTOR),
			emptyState: page.querySelector(EMPTY_SELECTOR),
			loadingState: page.querySelector(LOADING_SELECTOR),
			pageCount: page.querySelector(PAGE_COUNT_SELECTOR),
		};
	}

	function updatePageCount(els, total) {
		if (!els || !els.pageCount) return;
		els.pageCount.textContent =
			total > 0 ? `(${total} item${total === 1 ? "" : "s"})` : "";
	}

	function chunkArray(items, size) {
		const chunks = [];
		for (let i = 0; i < items.length; i += size) {
			chunks.push(items.slice(i, i + size));
		}
		return chunks;
	}

	function buildSearchUrl(idsChunk) {
		const query = idsChunk.map((id) => `id:${id}`).join(" OR ");
		return `/search?view=wishlist&type=product&q=${encodeURIComponent(query)}`;
	}

	function parseResultsFromHtml(html) {
		const parser = new DOMParser();
		const doc = parser.parseFromString(html, "text/html");
		const results = doc.querySelector(RESULTS_FRAGMENT_SELECTOR);
		return results ? results.innerHTML : null;
	}

	async function fetchWishlistChunk(idsChunk) {
		const url = buildSearchUrl(idsChunk);
		const response = await fetch(url, { credentials: "same-origin" });
		if (!response.ok) {
			throw new Error(`Wishlist search request failed: ${response.status}`);
		}
		const html = await response.text();
		const markup = parseResultsFromHtml(html);
		if (markup === null) {
			throw new Error("Wishlist search response is missing its results fragment");
		}
		return markup;
	}

	async function renderWishlistPage(ids) {
		const els = getWishlistPageElements();
		if (!els || !els.resultsContainer) return;

		els.resultsContainer.innerHTML = "";
		updatePageCount(els, ids.length);

		if (els.loadingState) els.loadingState.hidden = false;

		if (ids.length === 0) {
			if (els.emptyState) els.emptyState.hidden = false;
			if (els.loadingState) els.loadingState.hidden = true;
			return;
		}

		if (els.emptyState) els.emptyState.hidden = true;

		const chunks = chunkArray(ids, SEARCH_CHUNK_SIZE);
		let didLoadAllChunks = false;
		try {
			for (const chunk of chunks) {
				const markup = await fetchWishlistChunk(chunk);
				if (markup)
					els.resultsContainer.insertAdjacentHTML("beforeend", markup);
			}
			didLoadAllChunks = true;
		} catch (error) {
			els.resultsContainer.innerHTML =
				"<p>Unable to load saved items right now.</p>";
		} finally {
			if (els.loadingState) els.loadingState.hidden = true;
			let idsForUi = ids;
			if (didLoadAllChunks) {
				const rendered =
					els.resultsContainer.querySelectorAll(BUTTON_SELECTOR);
				const renderedIds = new Set();
				rendered.forEach(function (btn) {
					const rid = parseId(btn.getAttribute("data-product-id"));
					if (rid) renderedIds.add(rid);
				});
				idsForUi = ids.filter(function (id) {
					return renderedIds.has(id);
				});
				if (idsForUi.length !== ids.length) setIds(idsForUi);
			}
			updateButtons(idsForUi);
			updatePageCount(els, idsForUi.length);
			els.resultsContainer.dispatchEvent(
				new CustomEvent("theme:rimg:watch", { bubbles: true }),
			);
			patchCardButtons(els.resultsContainer);
		}
	}

	function patchCardButtons(container) {
		container
			.querySelectorAll(
				"[data-quickshop-slim], [data-quickshop-full], [data-quick-buy]",
			)
			.forEach(function (btn) {
				const card = btn.closest("[data-product-quickshop-url]");
				if (!card) return;
				const url = card.getAttribute("data-product-quickshop-url");
				if (!url || url === "#") return;
				const link = document.createElement("a");
				link.href = url;
				link.className = btn.className;
				link.innerHTML = btn.innerHTML;
				btn.replaceWith(link);
			});
	}

	function handleButtonClick(event) {
		const button = event.target.closest(BUTTON_SELECTOR);
		if (!button) return;

		event.preventDefault();
		const productId = parseId(button.getAttribute("data-product-id"));
		if (!productId) return;

		const wasActive = hasId(productId);
		const ids = toggleId(productId);
		updateGlobalUi(ids);

		const els = getWishlistPageElements();
		if (els) {
			if (wasActive) {
				const card =
					button.closest("[data-product-item]") ||
					button.closest(".productgrid--item");
				if (card) card.remove();
				updatePageCount(els, ids.length);
				if (ids.length === 0) {
					if (els.resultsContainer) els.resultsContainer.innerHTML = "";
					if (els.emptyState) els.emptyState.hidden = false;
					if (els.loadingState) els.loadingState.hidden = true;
				}
			} else {
				renderWishlistPage(ids);
			}
		}
	}

	function init() {
		const ids = getIds();
		updateGlobalUi(ids);

		document.addEventListener("click", handleButtonClick);

		document.addEventListener("wishlist:updated", (event) => {
			const nextIds = normalizeIds(
				event.detail && event.detail.ids ? event.detail.ids : [],
			);
			updateGlobalUi(nextIds);
		});

		if (document.querySelector(PAGE_CONTAINER_SELECTOR)) {
			renderWishlistPage(ids);
		}
	}

	window.Wishlist = {
		getIds,
		setIds,
		addId,
		removeId,
		toggleId,
		hasId,
		count,
	};

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", init);
	} else {
		init();
	}
})();
